import { motion } from "framer-motion"
import {
  RiArrowRightLine,
  RiHeartPulseFill,
  RiRocket2Fill,
  RiTeamFill,
  RiSparkling2Fill,
  RiMailLine,
} from "@remixicon/react"

import { ColorBand, ColorBandInner } from "@/components/landing/ColorBand"
import { MarketingPageShell } from "@/components/landing/MarketingPageShell"
import {
  fadeIn,
  fadeUp,
  inView,
  staggerContainer,
  staggerItem,
} from "@/components/landing/marketing-motion"
import { Button } from "@/components/ui/button"

const roles = [
  {
    title: "Growth & Marketing",
    type: "Full-time · Remote",
    body: "Help small businesses discover Sqaleshop and turn their sales link into a growth engine.",
  },
  {
    title: "Customer Support",
    type: "Full-time · Remote",
    body: "Be the calm voice for merchants, onboarding, troubleshooting, and celebrating their wins.",
  },
  {
    title: "Visual Designer",
    type: "Full-time · Remote",
    body: "Shape the look of Sqaleshop across product, marketing, and merchant-facing experiences.",
  },
  {
    title: "Business Development",
    type: "Full-time · Remote",
    body: "Build partnerships and pipelines that bring more sellers onto a link that actually sells.",
  },
] as const

const benefits = [
  {
    title: "Remote-first",
    body: "Work from where you do your best thinking. Async by default.",
    Icon: RiRocket2Fill,
  },
  {
    title: "Merchant obsession",
    body: "Every decision starts with how a seller feels at midnight in their DMs.",
    Icon: RiHeartPulseFill,
  },
  {
    title: "Small team, big ownership",
    body: "Ship visible work. Your name goes on the outcomes.",
    Icon: RiTeamFill,
  },
  {
    title: "Craft over hype",
    body: "We care about polish, clarity, and software people actually trust.",
    Icon: RiSparkling2Fill,
  },
] as const

export function CareersPage() {
  return (
    <MarketingPageShell
      eyebrow="Careers"
      title="Grow your career at Sqaleshop"
      description="Help merchants get their sales link, not another complicated ecommerce build."
      heroVariant="lavender"
      bare
      actions={
        <Button
          size="xl"
          className="rounded-full bg-primary px-8 font-semibold text-white shadow-none hover:bg-primary/90"
          asChild
        >
          <a href="#open-roles" className="gap-2">
            See open roles
            <RiArrowRightLine className="size-4" />
          </a>
        </Button>
      }
    >
    

      {/* Benefits */}
      <ColorBand className="bg-[#f6f4ef] text-primary">
        <ColorBandInner>
          <motion.h2
            {...fadeUp}
            className="font-display mx-auto max-w-2xl text-center text-3xl leading-[0.92] sm:text-4xl lg:text-[3rem]"
          >
            Thrive your way
          </motion.h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-base font-semibold text-primary/65">
            A few things that define how we work together.
          </p>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={inView}
            className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4"
          >
            {benefits.map((b) => (
              <motion.article
                key={b.title}
                variants={staggerItem}
                className="rounded-[2rem] bg-white p-6 sm:rounded-[2.5rem] sm:p-7"
              >
                <span className="inline-flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <b.Icon className="size-5" />
                </span>
                <h3 className="font-heading mt-5 text-lg text-primary">
                  {b.title}
                </h3>
                <p className="mt-2 text-sm font-semibold leading-relaxed text-primary/65">
                  {b.body}
                </p>
              </motion.article>
            ))}
          </motion.div>
        </ColorBandInner>
      </ColorBand>

      {/* Open roles */}
      <ColorBand id="open-roles" className="bg-white text-primary">
        <ColorBandInner>
          <motion.h2
            {...fadeUp}
            className="font-display text-3xl leading-[0.92] sm:text-4xl lg:text-[3rem]"
          >
            Open roles
          </motion.h2>
          <p className="mt-4 max-w-xl text-base font-semibold text-primary/65">
            Don&rsquo;t see a perfect fit? Write anyway, we hire for craft and
            curiosity.
          </p>
          <div className="mt-10 space-y-4">
            {roles.map((r, i) => (
              <motion.article
                key={r.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={inView}
                transition={{ delay: i * 0.05, duration: 0.45 }}
                className="flex flex-col gap-4 rounded-[2rem] bg-[#f6f4ef] p-6 sm:flex-row sm:items-center sm:justify-between sm:rounded-[2.5rem] sm:p-8"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-xs uppercase tracking-wider text-primary/55">
                    {r.type}
                  </p>
                  <h3 className="font-heading mt-1 text-xl text-primary">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-sm font-semibold leading-relaxed text-primary/65">
                    {r.body}
                  </p>
                </div>
                <Button
                  className="shrink-0 rounded-full px-6 font-semibold"
                  asChild
                >
                  <a
                    href={`mailto:hello@sqaleshop.com?subject=Application%20—%20${encodeURIComponent(r.title)}`}
                  >
                    Apply
                  </a>
                </Button>
              </motion.article>
            ))}
          </div>
        </ColorBandInner>
      </ColorBand>

      {/* Apply CTA */}
      <ColorBand className="bg-primary text-white">
        <div
          className="pointer-events-none absolute -left-20 top-10 size-80 rounded-full bg-[#be9afc]/25 blur-3xl"
          aria-hidden
        />
        <ColorBandInner className="text-center">
          <motion.h2
            {...fadeUp}
            className="font-display text-3xl leading-[0.92] sm:text-4xl lg:text-[3rem]"
          >
            Fitting in?
          </motion.h2>
          <p className="mx-auto mt-4 max-w-lg text-base font-semibold text-white/85">
            Send your CV, portfolio, or a short note about what you would like
            to build with us. We read everything.
          </p>
          <Button
            size="xl"
            className="mt-8 rounded-full bg-secondary px-8 font-semibold text-secondary-foreground shadow-none hover:bg-secondary/90 hover:text-secondary-foreground"
            asChild
          >
            <a
              href="mailto:hello@sqaleshop.com?subject=Careers%20at%20Sqaleup"
              className="gap-2"
            >
              <RiMailLine className="size-5" />
              hello@sqaleshop.com
            </a>
          </Button>
        </ColorBandInner>
      </ColorBand>
    </MarketingPageShell>
  )
}
